import React, { useState, useEffect } from 'react';
import { ArrowLeft, MapPin, Users, TrendingUp, Calendar, Map, Search, Hash } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import apiService from './services/api';
import { Panel, Section, Button, Badge, LoadingSpinner } from './components';

function LocationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [location, setLocation] = useState(null);
  const [technicians, setTechnicians] = useState([]);
  const [clients, setClients] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [zipSearch, setZipSearch] = useState('');
  const [servedZips, setServedZips] = useState([]);
  const [servedFilter, setServedFilter] = useState('');

  useEffect(() => {
    if (id) {
      fetchLocationDetails();
    }
  }, [id]);

  const fetchLocationDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch location details
      const locationData = await apiService.getLocation(id);
      setLocation(locationData);

      // Fetch technicians for this location
      const techData = await apiService.getTechnicians();
      const locationTechs = (techData.results || techData || []).filter(
        tech => tech.location?.id === parseInt(id)
      );
      setTechnicians(locationTechs);

      // Fetch served zips for this location
      try {
        const servedData = await apiService.getZipsServed();
        const locationServed = (servedData.results || []).filter(
          z => z.location === locationData.name
        );
        setServedZips(locationServed);
      } catch { /* ignore */ }

      // Get technician IDs for this location
      const technicianIds = locationTechs.map(tech => tech.id);

      // Fetch clients for this location
      const clientsData = await apiService.getClients({ location: id });
      setClients(clientsData.results || clientsData || []);

      // Fetch recent appointments for this location's technicians
      if (technicianIds.length > 0) {
        // Calculate date 3 months ago
        const threeMonthsAgo = new Date();
        threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
        const dateFilter = threeMonthsAgo.toISOString().split('T')[0];

        // Fetch appointments from last 3 months
        let allLocationAppts = [];

        // Fetch appointments for each technician
        for (const techId of technicianIds) {
          try {
            const techAppts = await apiService.getAppointments({
              technician: techId,
              limit: 100
            });
            const appts = techAppts.results || techAppts || [];
            allLocationAppts = [...allLocationAppts, ...appts];
          } catch (err) {
            console.error(`Error fetching appointments for technician ${techId}:`, err);
          }
        }

        // Sort by date and take latest 20
        const sortedAppts = allLocationAppts
          .sort((a, b) => new Date(b.date_time) - new Date(a.date_time))
          .slice(0, 20);

        setAppointments(sortedAppts);
      } else {
        setAppointments([]);
      }

    } catch (err) {
      setError(err.message || 'Failed to fetch location details');
      console.error('Error fetching location:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  return (
    <div className="flex-1 bg-neutral-50 overflow-y-auto" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
      <div className="p-3">
        {/* Header with Back Button */}
        <div className="mb-3 flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => navigate('/locations')}>
            <ArrowLeft className="w-3.5 h-3.5 mr-1" />
            Back
          </Button>
          <h1 className="text-lg font-semibold text-gray-900 leading-none">Location Details</h1>
        </div>

        {/* Loading State */}
        {loading && (
          <Panel className="p-6">
            <LoadingSpinner />
          </Panel>
        )}

        {/* Error State */}
        {error && !loading && (
          <Panel className="bg-red-50 border-red-200 mb-3">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-4 w-4 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-2">
                <h3 className="text-xs font-medium text-red-800">Error loading location</h3>
                <p className="mt-0.5 text-xs text-red-700">{error}</p>
                <button
                  onClick={fetchLocationDetails}
                  className="mt-1 text-xs font-medium text-red-800 hover:text-red-900"
                >
                  Try again
                </button>
              </div>
            </div>
          </Panel>
        )}

        {/* Content */}
        {!loading && !error && location && (
          <div className="space-y-3">
            {/* Location Overview */}
            <Panel>
              <Section title="Location Overview">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">Location Name</p>
                    <div className="flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5 text-red-800 flex-shrink-0" />
                      <p className="text-xs font-medium text-gray-900">{location.name || 'N/A'}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">Active Team Members</p>
                    <p className="text-xs font-medium text-gray-900">{location.technician_count || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">Total Clients</p>
                    <p className="text-xs font-medium text-gray-900">{location.client_count || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">Clients (3M)</p>
                    <p className="text-xs font-medium text-gray-900">{location.recent_client_count || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">Revenue (3M)</p>
                    <p className="text-xs font-semibold text-green-600">{formatCurrency(location.revenue_last_3_months)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">Avg Weekly Appts</p>
                    <p className="text-xs font-medium text-gray-900">{location.avg_weekly_appointments || 0}</p>
                  </div>
                </div>
              </Section>
            </Panel>

            {/* Technicians */}
            <Panel noPadding>
              <div className="px-3 py-2 border-b border-gray-200">
                <div className="flex items-center gap-1">
                  <Users className="w-3.5 h-3.5 text-gray-500" />
                  <h2 className="text-xs font-medium text-gray-900">Team Members ({technicians.length})</h2>
                </div>
              </div>
              {technicians.length === 0 ? (
                <p className="text-xs text-gray-500 text-center py-4">No team members assigned to this location</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Name</th>
                        <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Email</th>
                        <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Phone</th>
                        <th className="px-2 py-1.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wide">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {technicians.map((tech) => (
                        <tr
                          key={tech.id}
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => navigate(`/technicians/${tech.id}`)}
                        >
                          <td className="px-2 py-1.5 text-xs text-gray-900">
                            {tech.person?.first_name} {tech.person?.last_name}
                          </td>
                          <td className="px-2 py-1.5 text-xs text-gray-600">{tech.person?.email || 'N/A'}</td>
                          <td className="px-2 py-1.5 text-xs text-gray-600">{tech.person?.phone_number || 'N/A'}</td>
                          <td className="px-2 py-1.5 text-xs text-center">
                            <Badge variant={tech.internal_status === 'active' ? 'success' : 'neutral'} small>
                              {tech.internal_status || 'N/A'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>

            {/* Service Area Zip Codes */}
            {location.zip_codes && location.zip_codes.length > 0 && (
              <Panel noPadding>
                <div className="px-3 py-2 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <Map className="w-3.5 h-3.5 text-gray-500" />
                      <h2 className="text-xs font-medium text-gray-900">Service Area Zip Codes ({location.zip_codes.length})</h2>
                    </div>
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3" />
                      <input
                        type="text"
                        value={zipSearch}
                        onChange={(e) => setZipSearch(e.target.value.replace(/\D/g, '').slice(0, 5))}
                        placeholder="Filter..."
                        className="pl-6 pr-2 py-1 text-xs border border-gray-200 rounded w-28 focus:outline-none focus:ring-1 focus:ring-red-800"
                      />
                    </div>
                  </div>
                </div>
                <div className="p-3">
                  {(() => {
                    const filtered = zipSearch
                      ? location.zip_codes.filter(z => z.code.startsWith(zipSearch))
                      : location.zip_codes;
                    const grouped = filtered.reduce((acc, z) => {
                      const city = z.city || 'Unknown';
                      if (!acc[city]) acc[city] = [];
                      acc[city].push(z.code);
                      return acc;
                    }, {});
                    const cities = Object.keys(grouped).sort();
                    if (cities.length === 0) {
                      return <p className="text-xs text-gray-400 text-center py-2">No zip codes match "{zipSearch}"</p>;
                    }
                    return (
                      <div className="space-y-2">
                        {cities.map(city => (
                          <div key={city}>
                            <p className="text-xs font-medium text-gray-700 mb-1">{city} ({grouped[city].length})</p>
                            <div className="flex flex-wrap gap-1">
                              {grouped[city].map(code => (
                                <span key={code} className="px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-mono">
                                  {code}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </Panel>
            )}

            {/* Zips Served with Appointment Data */}
            {servedZips.length > 0 && (
              <Panel noPadding>
                <div className="px-3 py-2 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <Hash className="w-3.5 h-3.5 text-gray-500" />
                      <h2 className="text-xs font-medium text-gray-900">
                        Zips Served ({servedZips.length} · {servedZips.reduce((s, z) => s + z.appointment_count, 0).toLocaleString()} appts)
                      </h2>
                    </div>
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3" />
                      <input
                        type="text"
                        value={servedFilter}
                        onChange={(e) => setServedFilter(e.target.value)}
                        placeholder="Filter..."
                        className="pl-6 pr-2 py-1 text-xs border border-gray-200 rounded w-28 focus:outline-none focus:ring-1 focus:ring-red-800"
                      />
                    </div>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Zip</th>
                        <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">City</th>
                        <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Appts</th>
                        <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Nearest Tech</th>
                        <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Distance</th>
                        <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Last Served</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {servedZips
                        .filter(z => !servedFilter ||
                          z.zip_code.startsWith(servedFilter) ||
                          (z.city || '').toLowerCase().includes(servedFilter.toLowerCase())
                        )
                        .map((z) => (
                          <tr key={z.zip_code} className="hover:bg-gray-50">
                            <td className="px-2 py-1.5 text-xs font-mono font-medium text-gray-900">{z.zip_code}</td>
                            <td className="px-2 py-1.5 text-xs text-gray-700">{z.city || '—'}</td>
                            <td className="px-2 py-1.5 text-xs text-gray-900 text-right font-semibold">{z.appointment_count}</td>
                            <td className="px-2 py-1.5 text-xs text-gray-700">{z.nearest_tech || '—'}</td>
                            <td className="px-2 py-1.5 text-xs text-right">
                              {z.nearest_distance != null ? (
                                <span className={`inline-block px-1.5 py-0.5 rounded font-medium ${
                                  z.nearest_distance <= 15 ? 'text-green-700 bg-green-50' :
                                  z.nearest_distance <= 30 ? 'text-blue-700 bg-blue-50' :
                                  z.nearest_distance <= 60 ? 'text-amber-700 bg-amber-50' :
                                  'text-red-700 bg-red-50'
                                }`}>{z.nearest_distance} mi</span>
                              ) : '—'}
                            </td>
                            <td className="px-2 py-1.5 text-xs text-gray-500 text-right">
                              {z.last_appointment ? new Date(z.last_appointment).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
            )}

            {/* Recent Appointments */}
            <Panel noPadding>
              <div className="px-3 py-2 border-b border-gray-200">
                <div className="flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5 text-gray-500" />
                  <h2 className="text-xs font-medium text-gray-900">Recent Appointments (Last 20)</h2>
                </div>
              </div>
              {appointments.length === 0 ? (
                <p className="text-xs text-gray-500 text-center py-4">No recent appointments</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Date</th>
                        <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Team Member</th>
                        <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Client</th>
                        <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Revenue</th>
                        <th className="px-2 py-1.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wide">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {appointments
                        .sort((a, b) => new Date(b.date_time) - new Date(a.date_time))
                        .map((appt) => (
                          <tr
                            key={appt.id}
                            className="hover:bg-gray-50 cursor-pointer"
                            onClick={() => navigate(`/appointments/${appt.id}`)}
                          >
                            <td className="px-2 py-1.5 text-xs text-gray-900">{formatDate(appt.date_time)}</td>
                            <td className="px-2 py-1.5 text-xs text-gray-900">{appt.technician_name || 'N/A'}</td>
                            <td className="px-2 py-1.5 text-xs text-gray-900">{appt.client_name || 'N/A'}</td>
                            <td className="px-2 py-1.5 text-xs text-gray-900 text-right font-semibold">
                              {formatCurrency(appt.total_payment || appt.expected_revenue)}
                            </td>
                            <td className="px-2 py-1.5 text-xs text-center">
                              <Badge variant="neutral" small>
                                {appt.status || 'N/A'}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>

            {/* Top Clients */}
            <Panel noPadding>
              <div className="px-3 py-2 border-b border-gray-200">
                <div className="flex items-center gap-1">
                  <TrendingUp className="w-3.5 h-3.5 text-gray-500" />
                  <h2 className="text-xs font-medium text-gray-900">Top Clients by Revenue</h2>
                </div>
              </div>
              {clients.length === 0 ? (
                <p className="text-xs text-gray-500 text-center py-4">No clients in this location</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Name</th>
                        <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Email</th>
                        <th className="px-2 py-1.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wide">Visits</th>
                        <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Total Spent</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {clients
                        .sort((a, b) => parseFloat(b.total_spent || 0) - parseFloat(a.total_spent || 0))
                        .slice(0, 15)
                        .map((client) => (
                          <tr
                            key={client.id}
                            className="hover:bg-gray-50 cursor-pointer"
                            onClick={() => navigate(`/clients/${client.id}`)}
                          >
                            <td className="px-2 py-1.5 text-xs text-gray-900">{client.person_name || 'N/A'}</td>
                            <td className="px-2 py-1.5 text-xs text-gray-600">{client.person?.email || 'N/A'}</td>
                            <td className="px-2 py-1.5 text-xs text-gray-900 text-center">{client.appointment_count || 0}</td>
                            <td className="px-2 py-1.5 text-xs text-gray-900 text-right font-semibold">
                              {formatCurrency(client.total_spent)}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>
          </div>
        )}
      </div>
    </div>
  );
}

export default LocationDetailPage;
